import { useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useRealtime } from '../contexts/RealtimeContext';

/**
 * Watches realtime RBAC events for the signed-in user and refreshes their
 * session (role + effective permissions) so the sidebar and permission gates
 * update live — no manual re-login when an admin changes a role or a per-user
 * override. Must be mounted inside both AuthProvider and RealtimeProvider.
 * Renders nothing.
 *
 * Delivery is via the per-user queue `/user/queue/session.refresh` (server:
 * SessionRefreshListener). The previous implementation subscribed to the shared
 * `/topic/entities.{user_permission,app_role}` topics, but those carry other users'
 * permission data and are restricted to ADMIN/CEO — so for every non-admin user the
 * subscription was denied, which tore down the whole WebSocket and caused an infinite
 * reconnect storm. The per-user queue is authorized for any signed-in principal.
 */
export default function SessionPermissionWatcher() {
  const { isAuthenticated, refreshUser } = useAuth();
  const { subscribe } = useRealtime();

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    // The server only pushes here when THIS user's effective permissions change, so any
    // message means "re-fetch my session". The payload is informational only.
    return subscribe('/user/queue/session.refresh', () => {
      refreshUser();
    });
  }, [isAuthenticated, subscribe, refreshUser]);

  return null;
}
